import React, { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { CBadge, CButton, CCol, CFormCheck, CFormInput, CFormLabel, CRow } from "@coreui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faClose } from "@fortawesome/free-solid-svg-icons";
import { confirmAlert } from "react-confirm-alert";
import { toast } from "react-toastify";
import "react-confirm-alert/src/react-confirm-alert.css";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { useTranslationContext } from "@/context/TranslationContext";
import { breadcrumb_key } from "@/constants/translation/common.translation";
import { freelancerUser as freelancerUserRoutes } from "@/libs/routes";
import { setFormatDate } from "@/utils/dateFormate";
import { useListFreelancerUser, useGrantPremiumReward } from "@/hooks/useFreelancerUser";
import { useListServiceCategory } from "@/hooks/useServiceCategory";
import { useGetSettingData } from "@/hooks";
import { freelancerUser } from "@/constants/translation/freelancerUser.translation";
import { TNTable } from "@/components";
import CustomDropdown from "@/components/CustomDropdown";
import constants from "@/libs/constant";

const { dateTypes } = constants;

/**
 * Returns the bootstrap-style badge class for a given application/verification status,
 * matching the convention already shipped in src/pages/masters/report-reasons/list/index.js
 * (bg-warning/bg-success/bg-secondary/bg-danger) rather than reviving the unused StatusBadge
 * component.
 */
const getApplicationStatusBadgeClass = (status) => {
  switch (status) {
    case "approved":
    case "verified":
      return "bg-success";
    case "rejected":
      return "bg-danger";
    default:
      return "bg-warning text-dark";
  }
};

const getVerificationStatusBadgeClass = (status) => {
  switch (status) {
    case "approved":
      return "bg-success";
    case "rejected":
      return "bg-danger";
    case "pending":
      return "bg-warning text-dark";
    default:
      return "bg-secondary";
  }
};

const FreelancerUserList = () => {
  const t = useTranslationContext();
  const navigate = useNavigate();

  const [freelancerUserList, setFreelancerUserList] = useState([]);
  const [paginationData, setPaginationData] = useState(null);

  // Grant Premium Reward: own, independent page-local selection state (not shared with the
  // Normal User List page, not Redux/React Query).
  const [selectedIds, setSelectedIds] = useState(new Set());

  const [currentPage, setCurrentPage] = useState(1);
  const [searchText, setSearchText] = useState("");
  const [tempSearchText, setTempSearchText] = useState("");
  const [applicationStatus, setApplicationStatus] = useState("");
  const [verificationStatus, setVerificationStatus] = useState("");
  const [serviceStatus, setServiceStatus] = useState("");
  const [profileStatus, setProfileStatus] = useState("");
  const [professionId, setProfessionId] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [tableKey, setTableKey] = useState(0);
  const [sortField, setSortField] = useState("created_at");
  const [sortOrder, setSortOrder] = useState(constants.sort.order.desc);

  const searchTimeout = useRef(null);
  const isFiltering = useRef(false);

  useEffect(() => {
    return () => {
      if (searchTimeout.current) {
        clearTimeout(searchTimeout.current);
      }
    };
  }, []);

  const handleSearch = (e) => {
    const searchValue = e.target.value;
    setTempSearchText(searchValue);

    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }

    searchTimeout.current = setTimeout(() => {
      isFiltering.current = true;
      setSearchText(searchValue);
      setCurrentPage(1);
      setTableKey((prev) => prev + 1);

      setTimeout(() => {
        isFiltering.current = false;
      }, 200);
    }, 400);
  };

  const handleFilterChange = (setter) => (e) => {
    isFiltering.current = true;
    setter(e.target.value);
    setCurrentPage(1);
    setTableKey((prev) => prev + 1);

    setTimeout(() => {
      isFiltering.current = false;
    }, 200);
  };

  const handleSort = (field) => {
    isFiltering.current = true;

    if (sortField === field) {
      setSortOrder(sortOrder === constants.sort.order.asc ? constants.sort.order.desc : constants.sort.order.asc);
    } else {
      setSortField(field);
      setSortOrder(constants.sort.order.asc);
    }
    setCurrentPage(1);
    setTableKey((prev) => prev + 1);

    setTimeout(() => {
      isFiltering.current = false;
    }, 200);
  };

  const handleDateChange = (dateType, value) => {
    isFiltering.current = true;
    if (dateType === dateTypes.start) {
      setStartDate(value);
      if (endDate && value && endDate < value) {
        setEndDate(value);
      }
    } else {
      if (value && value < startDate) {
        setEndDate(startDate);
      } else {
        setEndDate(value);
      }
    }
    setCurrentPage(1);
    setTableKey((prev) => prev + 1);

    setTimeout(() => {
      isFiltering.current = false;
    }, 200);
  };

  const handleClearFilter = () => {
    isFiltering.current = true;

    setSearchText("");
    setTempSearchText("");
    setApplicationStatus("");
    setVerificationStatus("");
    setServiceStatus("");
    setProfileStatus("");
    setProfessionId("");
    setStartDate("");
    setEndDate("");
    setCurrentPage(1);
    setTableKey((prev) => prev + 1);

    setTimeout(() => {
      isFiltering.current = false;
    }, 200);
  };

  const {
    data: listData,
    isLoading,
    refetch,
  } = useListFreelancerUser({
    page: currentPage,
    limit: constants.pagination.defaultPageSize,
    ...(searchText && { search: searchText }),
    ...(applicationStatus && { application_status: applicationStatus }),
    ...(verificationStatus && { verification_status: verificationStatus }),
    ...(serviceStatus && { service_status: serviceStatus }),
    ...(profileStatus && { status: profileStatus }),
    ...(professionId && { profession_id: professionId }),
    ...(startDate && { date_from: startDate }),
    ...(endDate && { date_to: endDate }),
    sort_by: sortField,
    order_by: sortOrder,
  });

  const { data: professionListData } = useListServiceCategory({ limit: 100, status: "active" });

  // Reused as-is from the Settings page, per the implementation plan, so this list doesn't
  // duplicate the setting/list API call just to read the configured months value.
  const { data: settingsData } = useGetSettingData();
  const configuredPremiumMonths = settingsData?.data?.premium_grant_months;

  const { mutate: doGrantPremiumReward } = useGrantPremiumReward(() => {
    setSelectedIds(new Set());
    refetch();
  });

  useEffect(() => {
    if (listData) {
      setFreelancerUserList(listData.data?.freelancerUsers || []);
      setPaginationData({
        ...listData.data?.pagination,
        count: listData.data?.pagination?.total,
      });
    }
  }, [listData]);

  const professionOptions = useMemo(() => {
    const options = [{ value: "", label: t(freelancerUser.list.profession_filter_placeholder) }];
    (professionListData?.data?.serviceCategoryList || []).forEach((category) => {
      options.push({ value: category.id, label: category.name });
    });
    return options;
  }, [professionListData, t]);

  const applicationStatusOptions = useMemo(
    () => [
      { value: "", label: t(freelancerUser.list.application_status_filter.placeholder) },
      { value: "pending", label: t(freelancerUser.list.application_status_filter.pending) },
      { value: "approved", label: t(freelancerUser.list.application_status_filter.approved) },
      { value: "verified", label: t(freelancerUser.list.application_status_filter.verified) },
      { value: "rejected", label: t(freelancerUser.list.application_status_filter.rejected) },
    ],
    [t],
  );

  const verificationStatusOptions = useMemo(
    () => [
      { value: "", label: t(freelancerUser.list.verification_status_filter.placeholder) },
      { value: "pending", label: t(freelancerUser.list.verification_status_filter.pending) },
      { value: "approved", label: t(freelancerUser.list.verification_status_filter.approved) },
      { value: "rejected", label: t(freelancerUser.list.verification_status_filter.rejected) },
    ],
    [t],
  );

  const serviceStatusOptions = useMemo(
    () => [
      { value: "", label: t(freelancerUser.list.service_status_filter.placeholder) },
      { value: "pending", label: t(freelancerUser.list.service_status_filter.pending) },
      { value: "approved", label: t(freelancerUser.list.service_status_filter.approved) },
      { value: "rejected", label: t(freelancerUser.list.service_status_filter.rejected) },
    ],
    [t],
  );

  const profileStatusOptions = useMemo(
    () => [
      { value: "", label: t(freelancerUser.list.profile_status_filter.all) },
      { value: "deleted", label: t(freelancerUser.list.profile_status_filter.deleted) },
    ],
    [t],
  );

  const isDeletedProfileFilter = profileStatus === "deleted";

  const handleViewClick = (uuid) => {
    navigate(freelancerUserRoutes.view.replace(":id", uuid));
  };

  /**
   * Selection handlers for the "Grant Premium Reward" bulk action checkboxes
   */
  const handleToggleSelectRow = (uuid) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(uuid)) {
        next.delete(uuid);
      } else {
        next.add(uuid);
      }
      return next;
    });
  };

  const handleToggleSelectAllOnPage = (pageUuids) => {
    setSelectedIds((prev) => {
      const allSelected = pageUuids.length > 0 && pageUuids.every((uuid) => prev.has(uuid));
      const next = new Set(prev);
      if (allSelected) {
        pageUuids.forEach((uuid) => next.delete(uuid));
      } else {
        pageUuids.forEach((uuid) => next.add(uuid));
      }
      return next;
    });
  };

  /**
   * Handle "Grant Premium Reward" bulk action click
   */
  const handleGrantPremiumRewardClick = () => {
    const numericMonths = Number(configuredPremiumMonths);
    const isValidMonths =
      configuredPremiumMonths !== undefined &&
      configuredPremiumMonths !== null &&
      configuredPremiumMonths !== "" &&
      Number.isInteger(numericMonths) &&
      numericMonths >= 1;

    if (!isValidMonths) {
      toast.error(t(freelancerUser.list.premium_reward.months_not_configured_message));
      return;
    }

    const selectedUuids = Array.from(selectedIds);

    confirmAlert({
      customUI: ({ onClose }) => (
        <div className="alert-box">
          <div className="alert-popup">
            <FontAwesomeIcon className="alert-close" icon={faClose} onClick={onClose} />
            <h2 className="alert-title">{t(freelancerUser.list.premium_reward.confirm_heading)}</h2>
            <p className="text-center mb-3">
              {t(freelancerUser.list.premium_reward.confirm_message)
                .replace("{{months}}", numericMonths)
                .replace("{{count}}", selectedUuids.length)}
            </p>
            <div className="d-flex gap-2 justify-content-center flex-nowrap">
              <CButton
                color="primary"
                className="table-delete-button"
                onClick={() => {
                  doGrantPremiumReward(selectedUuids);
                  onClose();
                }}
              >
                {t(freelancerUser.list.premium_reward.confirm_yes_text)}
              </CButton>
              <CButton className="table-primary-button" color="danger" onClick={onClose}>
                {t(freelancerUser.list.premium_reward.confirm_no_text)}
              </CButton>
            </div>
          </div>
        </div>
      ),
    });
  };

  const columns = useMemo(() => {
    const currentPageUuids = freelancerUserList.map((row) => row.uuid);
    const allOnPageSelected =
      currentPageUuids.length > 0 && currentPageUuids.every((uuid) => selectedIds.has(uuid));

    return [
      {
        id: "select",
        header: () => (
          <CFormCheck
            checked={allOnPageSelected}
            onChange={() => handleToggleSelectAllOnPage(currentPageUuids)}
          />
        ),
        cell: ({ row }) => (
          <CFormCheck
            checked={selectedIds.has(row.original.uuid)}
            onChange={() => handleToggleSelectRow(row.original.uuid)}
          />
        ),
      },
      {
        id: "created_at",
        accessorKey: "createdAt",
        header: t(freelancerUser.list.table.created_at_col_heading),
        cell: ({ getValue }) => <span>{getValue() ? setFormatDate(getValue()) : t(freelancerUser.list.defaults.na)}</span>,
      },
      {
        id: "user_name",
        accessorKey: "userName",
        header: () => (
          <div onClick={() => handleSort("user_name")} style={{ cursor: "pointer" }}>
            {t(freelancerUser.list.table.user_name_col_heading)}
          </div>
        ),
        enableSorting: true,
        cell: ({ row }) => (
          <div>
            <div>{row.original.userName || t(freelancerUser.list.defaults.na)}</div>
            {row.original.businessName && row.original.businessName !== row.original.userName ? (
              <div className="text-muted small">{row.original.businessName}</div>
            ) : null}
          </div>
        ),
      },
      {
        id: "application_status",
        accessorKey: "applicationStatus",
        header: t(freelancerUser.list.table.application_status_col_heading),
        cell: ({ getValue }) => {
          const value = getValue();
          return (
            <span className={`badge ${getApplicationStatusBadgeClass(value)}`}>
              {t(freelancerUser.list.application_status_filter[value]) || value}
            </span>
          );
        },
      },
      {
        id: "verification_status",
        accessorKey: "verificationStatus",
        header: t(freelancerUser.list.table.verification_status_col_heading),
        cell: ({ getValue, row }) => {
          const value = getValue();
          return (
            <div className="d-flex align-items-center gap-2">
              <span className={`badge ${getVerificationStatusBadgeClass(value)}`}>
                {t(freelancerUser.list.verification_status_filter[value]) || value}
              </span>
            </div>
          );
        },
      },
      ...(isDeletedProfileFilter
        ? [
            {
              id: "deleted_at",
              accessorKey: "deletedAt",
              header: t(freelancerUser.list.table.deleted_at_col_heading),
              cell: ({ getValue }) => <span>{getValue() ? setFormatDate(getValue()) : t(freelancerUser.list.defaults.na)}</span>,
            },
          ]
        : []),
      {
        id: "premium_grant_count",
        accessorKey: "premium_grant_count",
        header: t(freelancerUser.list.table.reward_count_col_heading),
        cell: ({ getValue }) => <span>{getValue() ?? 0}</span>,
      },
      {
        id: "action",
        accessorKey: "uuid",
        header: t(freelancerUser.list.table.action_col_heading),
        cell: ({ row }) => (
          <div className="d-flex justify-content-center gap-3">
            <OverlayTrigger
              placement="top"
              overlay={<Tooltip id="view-tooltip">{t(freelancerUser.list.view_tooltip)}</Tooltip>}
            >
              <CButton
                onClick={() => handleViewClick(row.original.uuid)}
                style={{
                  backgroundColor: "#212631",
                  borderColor: "#212631",
                  color: "white",
                  padding: "0.25rem 0.5rem",
                  fontSize: "0.875rem",
                }}
              >
                <FontAwesomeIcon icon={faEye} />
              </CButton>
            </OverlayTrigger>
          </div>
        ),
      },
    ];
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [t, handleSort, isDeletedProfileFilter, freelancerUserList, selectedIds, handleToggleSelectAllOnPage, handleToggleSelectRow]);

  return (
    <>
      <CRow className="mb-4">
        <CCol lg={12} className="d-flex gap-2 justify-content-end flex-wrap">
          <CFormInput
            placeholder={t(freelancerUser.list.search_placeholder)}
            onChange={handleSearch}
            value={tempSearchText}
            className="form-control-sm"
            style={{ maxWidth: "260px" }}
          />

          <CustomDropdown
            className="form-control-sm"
            style={{ 
              maxWidth: '200px', 
              backgroundColor: '#212631', 
              borderColor: '#212631', 
              color: 'white'
            }}
            value={applicationStatus}
            onChange={handleFilterChange(setApplicationStatus)}
            options={applicationStatusOptions}
            placeholder={t(freelancerUser.list.application_status_filter.placeholder)}
          />

          <CustomDropdown
            className="form-control-sm"
            style={{ 
              maxWidth: '200px', 
              backgroundColor: '#212631', 
              borderColor: '#212631', 
              color: 'white'
            }}
            value={verificationStatus}
            onChange={handleFilterChange(setVerificationStatus)}
            options={verificationStatusOptions}
            placeholder={t(freelancerUser.list.verification_status_filter.placeholder)}
          />

          <CustomDropdown
            className="form-control-sm"
            style={{ 
              maxWidth: '200px', 
              backgroundColor: '#212631', 
              borderColor: '#212631', 
              color: 'white'
            }}
            value={serviceStatus}
            onChange={handleFilterChange(setServiceStatus)}
            options={serviceStatusOptions}
            placeholder={t(freelancerUser.list.service_status_filter.placeholder)}
          />

          <CustomDropdown
            className="form-control-sm"
            style={{
              maxWidth: '200px',
              backgroundColor: '#212631',
              borderColor: '#212631',
              color: 'white'
            }}
            value={professionId}
            onChange={handleFilterChange(setProfessionId)}
            options={professionOptions}
            placeholder={t(freelancerUser.list.profession_filter_placeholder)}
          />

          <CustomDropdown
            className="form-control-sm"
            style={{
              maxWidth: '200px',
              backgroundColor: '#212631',
              borderColor: '#212631',
              color: 'white'
            }}
            value={profileStatus}
            onChange={handleFilterChange(setProfileStatus)}
            options={profileStatusOptions}
            placeholder={t(freelancerUser.list.profile_status_filter.all)}
          />

          <div className="d-flex gap-2 align-items-center">
            <CFormLabel htmlFor="startDate" className="m-0">
              {t(freelancerUser.list.date_from_placeholder)}
            </CFormLabel>
            <CFormInput
              type="date"
              id="startDate"
              value={startDate}
              onChange={(e) => handleDateChange(dateTypes.start, e.target.value)}
              className="form-control"
              style={{ width: "155px", height: "45px" }}
            />
          </div>

          <div className="d-flex gap-2 align-items-center">
            <CFormLabel htmlFor="endDate" className="m-0">
              {t(freelancerUser.list.date_to_placeholder)}
            </CFormLabel>
            <CFormInput
              type="date"
              id="endDate"
              value={endDate}
              min={startDate}
              onChange={(e) => handleDateChange(dateTypes.end, e.target.value)}
              className="form-control"
              style={{ width: "155px", height: "45px" }}
            />
          </div>

          {(tempSearchText ||
            applicationStatus ||
            verificationStatus ||
            serviceStatus ||
            profileStatus ||
            professionId ||
            startDate ||
            endDate) && (
            <CButton color="primary" type="button" onClick={handleClearFilter} style={{ height: "45px" }}>
              {t(freelancerUser.list.clear_filter_btn_text)}
            </CButton>
          )}

          <CButton
            color="primary"
            type="button"
            onClick={handleGrantPremiumRewardClick}
            disabled={selectedIds.size === 0}
            style={{ height: "45px" }}
          >
            {t(freelancerUser.list.premium_reward.button_text)}
          </CButton>
        </CCol>
      </CRow>

      <TNTable
        key={tableKey}
        columns={columns}
        data={freelancerUserList}
        paginationData={paginationData}
        onSelectPage={(pageIndex) => {
          if (isFiltering.current) {
            return;
          }
          setCurrentPage(pageIndex + 1);
          window.scrollTo({ top: 0, behavior: "smooth" });
        }}
        t={t}
        pageIndexGet={currentPage - 1}
        isLoading={isLoading}
      />
    </>
  );
};

FreelancerUserList.breadCrumbArray = [
  {
    name: breadcrumb_key.freelancerUser?.list,
    pathname: freelancerUserRoutes.list,
    active: true,
  },
];

export default FreelancerUserList;
